import dayjs from "dayjs";
import { LoyaltyCardCashBonusPropsWithId } from "components/organisms/LoyaltyCardCashBonus/types";
import { LoyaltyFreeSpinsCardPropsWithId } from "components/organisms/LoyaltyFreeSpinsCard/types";
import { BodyAssignCashback } from "queries/useAssignCashback";
import { GameType } from "types/Games";
import { LoyaltyRewardUser } from "types/Loyalty";
import { RewardBonusNominal, RewardFreeSpins } from "types/Reward";
import { CashbackResponseInner } from "types/Cashback";
import {
  LoyaltyCardCashbackPropsCanGetCashback,
  LoyaltyCardCashbackPropsNeedPlay,
  LoyaltyCardCashbackPropsWithId,
  LoyaltyCashbackVariantTitle,
  UnionCashbackProps,
} from "components/organisms/LoyaltyCardCashback/types";
import { LoyaltyRewardsBFF } from "../types/LoyaltyBFF";
import {
  LoyaltyRewardMiniCardPropsWithId,
  LoyaltyRewardMiniCardVariant,
} from "../components/molecules/LoyaltyRewardMiniCard/types";

const formatFreeSpinProps = (
  rewardFreeSpin: RewardFreeSpins & { id: string; rewardId: string },
  gamesInfo: GameType[],
): LoyaltyFreeSpinsCardPropsWithId => {
  const game = gamesInfo.find(
    (gameInner) =>
      gameInner.uuid === rewardFreeSpin.rewardFreeSpin.freeSpinsGame,
  );

  const timeIsValid = dayjs(new Date())
    .add(rewardFreeSpin.rewardFreeSpin?.activationTerm, "hours")
    .isValid();

  const time = timeIsValid
    ? dayjs(new Date())
        .add(rewardFreeSpin.rewardFreeSpin?.activationTerm, "hours")
        .toISOString()
    : new Date().toISOString();

  return {
    id: rewardFreeSpin.id,
    variant: "access",
    countFreeSpins: rewardFreeSpin.rewardFreeSpin.freeSpinsCount,
    timeActivate: time,
    collapsible: false,
    gameName: game ? game.name : "unknown",
    imageGame: game
      ? `https://diswdgcu9cfva.cloudfront.net/covers2/${game.uuid}.webp`
      : undefined,
    handleClickActivate: () => {},
    rewardId: rewardFreeSpin.rewardId,
  };
};

type FormatRewardsUserLoyaltyOptions = {
  userRewards?: LoyaltyRewardUser[];
  infoRewards?: Array<RewardFreeSpins | RewardBonusNominal>;
  currency?: string;
  gameInfo?: GameType[];
  cashbackDaily?: CashbackResponseInner;
  cashbackWeekly?: CashbackResponseInner;
  cashbackMonthly?: CashbackResponseInner;
  assignCashback?: (body: BodyAssignCashback) => void;
};

const formatNominalProps = (
  rewardNominalUser: RewardBonusNominal & { id: string; rewardId: string },
  currencyCode: string,
): LoyaltyCardCashBonusPropsWithId => {
  const timeIsValid = dayjs(new Date())
    .add(rewardNominalUser.rewardNominal?.activationTerm, "hours")
    .isValid();

  const time = timeIsValid
    ? dayjs(new Date())
        .add(rewardNominalUser.rewardNominal?.activationTerm, "hours")
        .toISOString()
    : new Date().toISOString();

  return {
    id: rewardNominalUser.id,
    variant: "active",
    wager: rewardNominalUser.rewardNominal.wager,
    received:
      `${
        rewardNominalUser.rewardNominal.amount.find(
          (amo) => amo.currency === currencyCode,
        )?.value
      } ${currencyCode}` || "",
    expiredTime: time,
    handleClickActivate: () => {},
    rewardId: rewardNominalUser.rewardId,
  };
};

const formatReward = (
  reward:
    | (RewardFreeSpins & { id: string; rewardId: string })
    | (RewardBonusNominal & { id: string; rewardId: string }),
  gamesInfo: GameType[],
  currencyCode: string,
): LoyaltyFreeSpinsCardPropsWithId | LoyaltyCardCashBonusPropsWithId => {
  if (
    "rewardFreeSpin" in reward &&
    reward.rewardFreeSpin.freeSpinsCount !== undefined
  ) {
    return formatFreeSpinProps(reward, gamesInfo);
  }

  return formatNominalProps(
    reward as RewardBonusNominal & { id: string; rewardId: string },
    currencyCode,
  );
};

const formatCashbackProps = (
  currency: string,
  rewardCashback?: CashbackResponseInner,
  onAssignCashback?: (body: BodyAssignCashback) => void,
): LoyaltyCardCashbackPropsWithId | null => {
  if (rewardCashback === undefined) {
    return null;
  }

  const currentAccumulatedAmountUser =
    ((rewardCashback.rewardCashbackValue.value / 100) *
      rewardCashback.rewardCashback.percent) /
    100;
  const minAmountCashback =
    rewardCashback.rewardCashback.minCashback[currency] / 100;
  const maxAmountCashback =
    rewardCashback.rewardCashback.maxCashback[currency] / 100;
  const { id, wager, cashbackVariant } = rewardCashback.rewardCashback;
  const title: LoyaltyCashbackVariantTitle = cashbackVariant;
  const showAmount =
    currentAccumulatedAmountUser >= maxAmountCashback
      ? maxAmountCashback
      : currentAccumulatedAmountUser;

  let cashbackProps:
    | LoyaltyCardCashbackPropsCanGetCashback
    | LoyaltyCardCashbackPropsNeedPlay
    | null = null;

  const unionCashbackProps: UnionCashbackProps = {
    title,
    expiredTime: undefined,
    loss: `${rewardCashback.rewardCashback.percent}%`,
    image:
      // eslint-disable-next-line sonarjs/no-duplicate-string
      "https://diswdgcu9cfva.cloudfront.net/loyalty/rewards-rank/daily-cashback-merged.webp",
    collapsible: false,
    handleClickAssign: () =>
      onAssignCashback?.({
        rewardId: rewardCashback.rewardCashback.id,
        source: "loyalty",
        triggerCompainId: rewardCashback.rewardCashbackValue.triggerId,
      }),
  };

  if (currentAccumulatedAmountUser >= minAmountCashback) {
    cashbackProps = {
      variant: "canGetCashback",
      wager,
      accumulatedCashback: `${showAmount} ${currency}`,
      ...unionCashbackProps,
    };
  } else {
    cashbackProps = {
      variant: "needPlay",
      minAmount: `${minAmountCashback} ${currency}`,
      wager,
      ...unionCashbackProps,
    };
  }

  return {
    id,
    ...cashbackProps,
  };
};

export const formatRewardsUserLoyalty = (
  options: FormatRewardsUserLoyaltyOptions,
): Array<
  | LoyaltyCardCashBonusPropsWithId
  | LoyaltyFreeSpinsCardPropsWithId
  | LoyaltyCardCashbackPropsWithId
> => {
  const {
    userRewards,
    infoRewards,
    gameInfo,
    currency,
    cashbackDaily,
    cashbackWeekly,
    cashbackMonthly,
    assignCashback,
  } = options;

  if (!userRewards || !infoRewards || !gameInfo || !currency) {
    return [];
  }

  const userRewardsFormatted = userRewards
    .filter((userReward) => userReward.status !== "assigned")
    .reduce<
      Array<
        | (RewardFreeSpins & { id: string; rewardId: string })
        | (RewardBonusNominal & { id: string; rewardId: string })
      >
    >((acc, cur) => {
      const reward = infoRewards.find(
        (infoReward) => infoReward.reward.id === cur.rewardId,
      );

      if (!reward) {
        return acc;
      }

      return [...acc, { ...reward, id: cur.id, rewardId: cur.rewardId }];
    }, []);

  const rewardsPropsBonusEcosystem = userRewardsFormatted.map((reward) =>
    formatReward(reward, gameInfo, currency),
  );

  return [
    formatCashbackProps(currency, cashbackDaily, assignCashback),
    formatCashbackProps(currency, cashbackWeekly, assignCashback),
    formatCashbackProps(currency, cashbackMonthly, assignCashback),
    ...rewardsPropsBonusEcosystem,
  ].filter((rew) => rew !== null) as Array<
    | LoyaltyCardCashBonusPropsWithId
    | LoyaltyFreeSpinsCardPropsWithId
    | LoyaltyCardCashbackPropsWithId
  >;
};

export const mapRewards = (
  rewards?: LoyaltyRewardsBFF,
): LoyaltyRewardMiniCardPropsWithId[] => {
  if (!rewards) {
    return [];
  }

  const {
    mascot = undefined,
    rewardFreeSpin = undefined,
    rewardNominal = undefined,
    rewardCashbackDaily = undefined,
    rewardCashbackWeekly = undefined,
    rewardCashbackMonthly = undefined,
    booster = undefined,
  } = rewards;

  const freeSpins: LoyaltyRewardMiniCardPropsWithId | null = rewardFreeSpin
    ? {
        id: rewardFreeSpin.rewardId,
        value: String(rewardFreeSpin.rewardInfo.freeSpinsCount),
        variant: LoyaltyRewardMiniCardVariant.FREE_SPINS,
        image: `https://diswdgcu9cfva.cloudfront.net/covers2/${rewardFreeSpin.freeSpinsGame.urlAlias}.webp`,
        imageStatic:
          "https://diswdgcu9cfva.cloudfront.net/loyalty/reward/slots.webp",
        freeSpinsGame: rewardFreeSpin.freeSpinsGame.name,
      }
    : null;

  const nominal: LoyaltyRewardMiniCardPropsWithId | null = rewardNominal
    ? {
        id: rewardNominal.rewardId,
        value: String(rewardNominal.rewardInfo.amountByCurrency / 100),
        variant: LoyaltyRewardMiniCardVariant.NOMINAL,
        image:
          "https://diswdgcu9cfva.cloudfront.net/loyalty/rewards-rank/cash-bonus-merged.webp",
      }
    : null;

  const cashbackDaily: LoyaltyRewardMiniCardPropsWithId | null =
    rewardCashbackDaily
      ? {
          id: rewardCashbackDaily.rewardId,
          value: `${String(rewardCashbackDaily.rewardInfo.percent)}%`,
          variant: LoyaltyRewardMiniCardVariant.CASHBACK_DAILY,
          image:
            "https://diswdgcu9cfva.cloudfront.net/loyalty/rewards-rank/daily-cashback-merged.webp",
        }
      : null;

  const cashbackWeekly: LoyaltyRewardMiniCardPropsWithId | null =
    rewardCashbackWeekly
      ? {
          id: rewardCashbackWeekly.rewardId,
          value: `${String(rewardCashbackWeekly.rewardInfo.percent)}%`,
          variant: LoyaltyRewardMiniCardVariant.CASHBACK_WEEKLY,
          image:
            "https://diswdgcu9cfva.cloudfront.net/loyalty/rewards-rank/weekly-cashback-merged.webp",
        }
      : null;

  const cashbackMonthly: LoyaltyRewardMiniCardPropsWithId | null =
    rewardCashbackMonthly
      ? {
          id: rewardCashbackMonthly.rewardId,
          value: `${String(rewardCashbackMonthly.rewardInfo.percent)}%`,
          variant: LoyaltyRewardMiniCardVariant.CASHBACK_MONTHLY,
          image:
            "https://diswdgcu9cfva.cloudfront.net/loyalty/rewards-rank/daily-cashback-merged.webp",
        }
      : null;

  const mascotCard: LoyaltyRewardMiniCardPropsWithId | null = mascot
    ? {
        id: mascot.rewardId,
        value: "",
        variant: LoyaltyRewardMiniCardVariant.MASCOT,
        image:
          "https://diswdgcu9cfva.cloudfront.net/loyalty/rewards-rank/new-avatar-skin-merged.webp",
      }
    : null;

  const boosterCard: LoyaltyRewardMiniCardPropsWithId | null = booster
    ? {
        id: booster.rewardId,
        value: `${booster.rewardInfo.multiplier}x`,
        variant: LoyaltyRewardMiniCardVariant.BOOSTER,
        image:
          "https://diswdgcu9cfva.cloudfront.net/loyalty/rewards-rank/booster-racket.webp",
      }
    : null;

  return [
    nominal,
    freeSpins,
    mascotCard,
    cashbackDaily,
    cashbackWeekly,
    cashbackMonthly,
    boosterCard,
  ].filter((card) => card !== null) as LoyaltyRewardMiniCardPropsWithId[];
};
